import {
  Box,
  Radio,
  Tooltip,
  Typography,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import React from "react";
import { Group } from "@/modules/lead-page/dtos/index-v2";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { capitalizeWords } from "@/modules/lead-page/components/lead-info/lead-actions/merge-lead/utils";

const NOT_SET = "not set";

interface Props {
  group: Group;
  selections: Record<string, any>;
  handleSelectionChange: (
    groupId: string,
    type: "referral" | "location" | "methodOfContact",
    value: string
  ) => void;
}

const LeadTypeContent: React.FC<Props> = ({
  group,
  selections,
  handleSelectionChange,
}) => {
  const renderLeadTypeLabel = (label: string) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
      }}
    >
      <Typography
        sx={{
          ...nawyTheme.nawy.typography.english.variants.body.sm.semibold,
          color: nawyTheme.nawy.colors.semantic.text.primary,
        }}
      >
        {`${capitalizeWords(label)} Lead`}
      </Typography>
    </Box>
  );

  const renderRadioItem = (
    label: string,
    options: { label: any; value: string }[],
    groupId: string,
    type: "referral" | "location" | "methodOfContact"
  ) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: "8px",
      }}
    >
      <Typography
        sx={{
          ...nawyTheme.nawy.typography.english.variants.body.xs.semibold,
          color: nawyTheme.nawy.colors.semantic.text.secondary,
        }}
      >
        {label}
      </Typography>

      <RadioGroup
        row
        value={selections[groupId]?.[type] || ""}
        onChange={(e) => handleSelectionChange(groupId, type, e.target.value)}
        sx={{
          width: "100%",
          minHeight: "20px",
          alignItems: "center",
        }}
      >
        {options.map(({ label, value }, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              gap: 2,
              width: "50%",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              value={value}
              control={<Radio sx={{ p: 0, mr: "8px" }} />}
              label={label}
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": {
                  ...nawyTheme.nawy.typography.english.variants.body.md.regular,
                  color: nawyTheme.nawy.colors.semantic.text.primary,
                },
              }}
            />
          </Box>
        ))}
      </RadioGroup>
    </Box>
  );

  const getReferralOption = (entity: any) => {
    const referral = entity?.referral?.find((r: any) => !r.isExpired);
    if (!referral) return { label: NOT_SET, value: NOT_SET };

    const referrerName =
      referral?.referrer?.name.toLowerCase() || "Unknown Referrer";

    const validityText = referral.validityExpirationDate
      ? (() => {
          const expiry = new Date(referral.validityExpirationDate);
          const days = Math.ceil(
            (expiry.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
          );
          return `Valid for ${days} days`;
        })()
      : null;

    const subInfo = [validityText].filter(Boolean).join(" • ");

    const tooltipContent = (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {referral?.budget && (
          <Typography variant="caption">
            <strong>Expected Budget:</strong> {referral.budget}
          </Typography>
        )}
        {referral?.numberOfUnits && (
          <Typography variant="caption">
            <strong>Number Of Units:</strong> {referral.numberOfUnits}
          </Typography>
        )}
        <Typography variant="caption">
          <strong>Agent Email:</strong> {referral?.referrer?.email || "N/A"}
        </Typography>
        <Typography variant="caption">
          <strong>Agent Phone:</strong>{" "}
          {referral?.referrer?.phoneNumber || "N/A"}
        </Typography>
      </Box>
    );

    const label = (
      <Tooltip title={tooltipContent} placement="top">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Typography
            component="div"
            sx={{
              ...nawyTheme.nawy.typography.english.variants.body.md.regular,
              color: nawyTheme.nawy.colors.semantic.text.primary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {referrerName}
          </Typography>
          {subInfo && (
            <Typography
              component="div"
              sx={{
                ...nawyTheme.nawy.typography.english.variants.body.xxs.regular,
                color: nawyTheme.nawy.colors.semantic.text.secondary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {subInfo}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );

    return {
      label,
      value: referral?.referrer?.uid || NOT_SET,
    };
  };

  const renderLocationOption = (entity: any) => ({
    label: entity?.location?.name.toLowerCase() || NOT_SET,
    value: entity?.location?.name || NOT_SET,
  });

  const renderMethodOption = (entity: any) => ({
    label: entity?.methodOfContact?.name.toLowerCase() || NOT_SET,
    value: entity?.methodOfContact?.uid || NOT_SET,
  });

  const renderSingleEntityView = (entity: any, isParent: boolean) => {
    const sidePadding = {
      pl: isParent ? 0 : "50%",
      pr: isParent ? "50%" : 0,
    };

    const referralOption = getReferralOption(entity);
    const locationOption = renderLocationOption(entity);
    const methodOption = renderMethodOption(entity);

    const renderStaticRadioGroup = (
      label: string,
      option: { label: any; value: string }
    ) => (
      <Box
        sx={{
          gap: "8px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          sx={{
            ...nawyTheme.nawy.typography.english.variants.body.xs.semibold,
            color: nawyTheme.nawy.colors.semantic.text.secondary,
          }}
        >
          {label}
        </Typography>

        <RadioGroup
          row
          value={option.value}
          sx={{
            width: "100%",
            alignItems: "center",
            ...sidePadding,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flex: 1,
              minWidth: 0,
            }}
          >
            <FormControlLabel
              value={option.value}
              control={<Radio checked disabled sx={{ p: 0, mr: "8px" }} />}
              label={option.label}
              sx={{
                m: 0,
                width: "100%",
                overflow: "hidden",
                "& .MuiFormControlLabel-label": {
                  ...nawyTheme.nawy.typography.english.variants.body.md.regular,
                  color: nawyTheme.nawy.colors.semantic.text.primary,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          </Box>
        </RadioGroup>
      </Box>
    );

    const renderTagLabel = () => {
      const isParentTag = isParent;

      const backgroundColor = isParentTag
        ? nawyTheme.nawy.colors.semantic.layer.secondary
        : "#FFF4E5";

      const textColor = isParentTag
        ? nawyTheme.nawy.colors.semantic.text.accent
        : "#8A4B00";

      const label = isParentTag
        ? "Existing lead on parent customer"
        : "Will be moved to parent customer";

      return (
        <Box
          sx={{
            backgroundColor,
            borderRadius: "16px",
            px: 1,
            py: 0.5,
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            sx={{
              ...nawyTheme.nawy.typography.english.variants.body.xxs.regular,
              color: textColor,
            }}
          >
            {label}
          </Typography>
        </Box>
      );
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: nawyTheme.nawy.spacing.xs,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {renderLeadTypeLabel(group.type.name)}
          {renderTagLabel()}
        </Box>

        {renderStaticRadioGroup("Referral", referralOption)}
        {renderStaticRadioGroup("Location", locationOption)}
        {renderStaticRadioGroup("Method of contact", methodOption)}
      </Box>
    );
  };

  const entities = [group.parent, group.child].filter(Boolean);

  if (entities.length === 2) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <Box>{renderLeadTypeLabel(group.type.name)}</Box>

        {renderRadioItem(
          "Referral",
          [getReferralOption(group.parent), getReferralOption(group.child)],
          group.type.uid,
          "referral"
        )}

        {renderRadioItem(
          "Location",
          [
            renderLocationOption(group.parent),
            renderLocationOption(group.child),
          ],
          group.type.uid,
          "location"
        )}

        {renderRadioItem(
          "Method of contact",
          [renderMethodOption(group.parent), renderMethodOption(group.child)],
          group.type.uid,
          "methodOfContact"
        )}
      </Box>
    );
  }

  const entity = group.parent || group.child;
  return renderSingleEntityView(entity, !!group.parent);
};

export default LeadTypeContent;
